import { StructDeserializeStream, TransformStream, } from "@yume-chan/stream-extra";
import Struct from "@yume-chan/struct";
import { AndroidKeyEventAction, ScrcpyControlMessageType, } from "../../control/index.js";
import { toScrcpyOptionValue } from "../types.js";
import { CodecOptions } from "./codec-options.js";
import { parseH264Configuration, parseSequenceParameterSet, removeH264Emulation, } from "./h264-configuration.js";
import { ScrcpyScrollController1_16 } from "./scroll.js";
export var ScrcpyLogLevel;
(function (ScrcpyLogLevel) {
    ScrcpyLogLevel["Verbose"] = "verbose";
    ScrcpyLogLevel["Debug"] = "debug";
    ScrcpyLogLevel["Info"] = "info";
    ScrcpyLogLevel["Warn"] = "warn";
    ScrcpyLogLevel["Error"] = "error";
})(ScrcpyLogLevel = ScrcpyLogLevel || (ScrcpyLogLevel = {}));
export var ScrcpyVideoOrientation;
(function (ScrcpyVideoOrientation) {
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["Initial"] = -2] = "Initial";
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["Unlocked"] = -1] = "Unlocked";
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["Portrait"] = 0] = "Portrait";
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["Landscape"] = 1] = "Landscape";
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["PortraitFlipped"] = 2] = "PortraitFlipped";
    ScrcpyVideoOrientation[ScrcpyVideoOrientation["LandscapeFlipped"] = 3] = "LandscapeFlipped";
})(ScrcpyVideoOrientation = ScrcpyVideoOrientation || (ScrcpyVideoOrientation = {}));
export const VideoPacket = new Struct()
    .uint64("pts")
    .uint32("size")
    .uint8Array("data", { lengthField: "size" });
export const NO_PTS = BigInt(1) << BigInt(63);
export const ScrcpyBackOrScreenOnControlMessage1_16 = new Struct().uint8("type", ScrcpyControlMessageType.BackOrScreenOn);
export const ScrcpySetClipboardControlMessage1_15 = new Struct()
    .uint8("type", ScrcpyControlMessageType.SetClipboard)
    .uint32("length")
    .string("content", { lengthField: "length" });
export class ScrcpyOptions1_16 {
    value;
    constructor(value) {
        if (new.target === ScrcpyOptions1_16 &&
            value.logLevel === ScrcpyLogLevel.Verbose) {
            value.logLevel = ScrcpyLogLevel.Debug;
        }
        if (new.target === ScrcpyOptions1_16 &&
            value.lockVideoOrientation === ScrcpyVideoOrientation.Initial) {
            value.lockVideoOrientation = ScrcpyVideoOrientation.Unlocked;
        }
        this.value = value;
    }
    getArgumentOrder() {
        return [
            "logLevel",
            "maxSize",
            "bitRate",
            "maxFps",
            "lockVideoOrientation",
            "tunnelForward",
            "crop",
            "sendFrameMeta",
            "control",
            "displayId",
            "showTouches",
            "stayAwake",
            "codecOptions",
            "encoderName",
        ];
    }
    getDefaultValue() {
        return {
            logLevel: ScrcpyLogLevel.Debug,
            maxSize: 0,
            bitRate: 8000000,
            maxFps: 0,
            lockVideoOrientation: ScrcpyVideoOrientation.Unlocked,
            tunnelForward: false,
            crop: "-",
            sendFrameMeta: true,
            control: true,
            displayId: 0,
            showTouches: false,
            stayAwake: false,
            codecOptions: new CodecOptions({}),
            encoderName: "-",
        };
    }
    formatServerArguments() {
        const defaults = this.getDefaultValue();
        return this.getArgumentOrder().map((key) => toScrcpyOptionValue(this.value[key] || defaults[key], "-"));
    }
    getOutputEncoderNameRegex() {
        return /\s+scrcpy --encoder-name '(.*?)'/;
    }
    createVideoStreamTransformer() {
        // Optimized path for video frames only
        if (this.value.sendFrameMeta === false) {
            return new TransformStream({
                transform(chunk, controller) {
                    controller.enqueue({
                        type: "frame",
                        data: chunk,
                    });
                },
            });
        }
        let header;
        const deserializeStream = new StructDeserializeStream(VideoPacket);
        return {
            writable: deserializeStream.writable,
            readable: deserializeStream.readable.pipeThrough(new TransformStream({
                transform(packet, controller) {
                    if (packet.pts === NO_PTS) {
                        const { sequenceParameterSet, pictureParameterSet, } = parseH264Configuration(packet.data);
                        const { profile_idc: profileIndex, constraint_set: constraintSet, level_idc: levelIndex, pic_width_in_mbs_minus1, pic_height_in_map_units_minus1, frame_mbs_only_flag, frame_crop_left_offset, frame_crop_right_offset, frame_crop_top_offset, frame_crop_bottom_offset, } = parseSequenceParameterSet(removeH264Emulation(sequenceParameterSet));
                        const encodedWidth = (pic_width_in_mbs_minus1 + 1) * 16;
                        const encodedHeight = (pic_height_in_map_units_minus1 + 1) *
                            (2 - frame_mbs_only_flag) *
                            16;
                        const cropLeft = frame_crop_left_offset * 2;
                        const cropRight = frame_crop_right_offset * 2;
                        const cropTop = frame_crop_top_offset * 2;
                        const cropBottom = frame_crop_bottom_offset * 2;
                        const croppedWidth = encodedWidth - cropLeft - cropRight;
                        const croppedHeight = encodedHeight - cropTop - cropBottom;
                        header = packet.data;
                        controller.enqueue({
                            type: "configuration",
                            pictureParameterSet,
                            sequenceParameterSet,
                            data: {
                                profileIndex,
                                constraintSet,
                                levelIndex,
                                encodedWidth,
                                encodedHeight,
                                cropLeft,
                                cropRight,
                                cropTop,
                                cropBottom,
                                croppedWidth,
                                croppedHeight,
                            },
                        });
                        return;
                    }
                    let frameData;
                    if (header) {
                        frameData = new Uint8Array(header.byteLength + packet.data.byteLength);
                        frameData.set(header);
                        frameData.set(packet.data, header.byteLength);
                        header = undefined;
                    }
                    else {
                        frameData = packet.data;
                    }
                    controller.enqueue({
                        type: "frame",
                        pts: packet.pts,
                        data: frameData,
                    });
                },
            })),
        };
    }
    getControlMessageTypes() {
        return [
            /*  0 */ ScrcpyControlMessageType.InjectKeyCode,
            /*  1 */ ScrcpyControlMessageType.InjectText,
            /*  2 */ ScrcpyControlMessageType.InjectTouch,
            /*  3 */ ScrcpyControlMessageType.InjectScroll,
            /*  4 */ ScrcpyControlMessageType.BackOrScreenOn,
            /*  5 */ ScrcpyControlMessageType.ExpandNotificationPanel,
            /*  6 */ ScrcpyControlMessageType.CollapseNotificationPanel,
            /*  7 */ ScrcpyControlMessageType.GetClipboard,
            /*  8 */ ScrcpyControlMessageType.SetClipboard,
            /*  9 */ ScrcpyControlMessageType.SetScreenPowerMode,
            /* 10 */ ScrcpyControlMessageType.RotateDevice,
        ];
    }
    serializeBackOrScreenOnControlMessage(message) {
        if (message.action === AndroidKeyEventAction.Down) {
            return ScrcpyBackOrScreenOnControlMessage1_16.serialize(message);
        }
        return undefined;
    }
    serializeSetClipboardControlMessage(message) {
        return ScrcpySetClipboardControlMessage1_15.serialize(message);
    }
    getScrollController() {
        return new ScrcpyScrollController1_16();
    }
}
//# sourceMappingURL=options.js.map